import * as React from "react";
import Input from "../Input-new/Input";
import SelectFirm from "../SelectFirm/SelectFirm";
import SelectInvestor from "../SelectInvestor/SelectInvestor";
import Checkbox from "../CheckboxNew";
import LinkComp from "../LinkComp";
import OpenInCrmButton from "../OpenInCrmButton/OpenInCrmButton";
import DeceasedPill, { isDeceased } from "../DeceasedPill/DeceasedPill";
import { formatDateUSA, formatPhoneNumber, isOn } from "../../utils";

/** The newest note, read-only, the way the vendor page shows its own; Show Notes has them all. */
function latestNote(notes) {
	if (!notes || !notes.length) return null;

	const note = notes[notes.length - 1];
	const stamped = formatDateUSA(note.created_at, true);
	const who = (note.author || '').trim();

	return {
		when: stamped ? stamped.replace(' ', ', ') + (who ? ` - ${who}` : '') : '',
		text: note.text,
	};
}

const HotListButton = ({ isHotList, isHotListBusy, onHotListToggle, deleted }) => {
	if (deleted) return null;

	const active = Boolean(isHotList);

	return (
		<button
			onClick={() => !isHotListBusy && onHotListToggle(!active)}
			disabled={isHotListBusy}
			title={active ? 'Remove from your Hot List' : 'Add to your Hot List'}
			style={{
				display: 'inline-flex',
				alignItems: 'center',
				gap: '5px',
				padding: '5px 13px',
				borderRadius: '6px',
				border: `1.5px solid ${active ? '#c62828' : '#bdbdbd'}`,
				backgroundColor: active ? '#ffebee' : '#fafafa',
				color: active ? '#c62828' : '#757575',
				cursor: isHotListBusy ? 'wait' : 'pointer',
				opacity: isHotListBusy ? 0.6 : 1,
				fontWeight: '600',
				fontSize: '12px',
				lineHeight: '1',
				letterSpacing: '0.3px',
				transition: 'all 0.15s ease',
				whiteSpace: 'nowrap',
				flexShrink: 0,
			}}
		>
			<span style={{ fontSize: '14px', lineHeight: '1' }}>
				{active ? '★' : '☆'}
			</span>
			{isHotListBusy ? 'Processing...' : 'Hot List'}
		</button>
	);
};

const InvestorInfo = ({
	data,
	handleChangeInput,
	handleChangeDropdown,
	viewMode,
	deleted,
	addNewItem,
	newFirm,
	isHotList,
	isHotListBusy,
	onHotListToggle,
	notes,
	notesError,
	onShowNotes,
	secondContact,
	onDeceasedChange,
	onActiveBuyerChange,
	onSecondContactChange,
}) => {
	const hasLeadId = Boolean(data.LEAD_ID);
	const deceased = isDeceased(data);
	const activeBuyer = isOn(data.ACTIVE_BUYER);
	const latest = latestNote(notes);

	return (
		<div className="Investor-info" style={{padding: '0'}}>
			<div style={{
				backgroundColor: '#ffffff',
				borderRadius: '8px',
				padding: '24px',
				marginBottom: '20px',
				border: '1px solid #e8eaed'
			}}>
				<div style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					marginBottom: '20px',
					paddingBottom: '16px',
					borderBottom: '1px solid #e8eaed',
				}}>
					<h3 style={{
						margin: 0,
						fontSize: '16px',
						fontWeight: '600',
						color: '#202124',
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
					}}>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
							<circle cx="12" cy="7" r="4"></circle>
						</svg>
						<span>
							Investor Information
							<DeceasedPill investor={data} />
						</span>
					</h3>
					<HotListButton
						isHotList={isHotList}
						isHotListBusy={isHotListBusy}
						onHotListToggle={onHotListToggle}
						deleted={deleted}
					/>
				</div>
				
				<div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px'}}>
					<Input
						valueType="text"
						name="NAME"
						label="Name *"
						value={data.NAME}
						handleParentChange={handleChangeInput}
						viewMode={viewMode}
						deleted={deleted}
						required={true}
					/>
					<Input
						valueType="email"
						name="EMAIL"
						label="Email"
						value={data.EMAIL}
						handleParentChange={handleChangeInput}
						viewMode={viewMode}
						deleted={deleted}
					/>
					<Input
						valueType="phone"
						name="CELL"
						label="Cell"
						value={data.CELL}
						handleParentChange={handleChangeInput}
						viewMode={viewMode}
						deleted={deleted}
					/>
					<Input
						valueType="phone"
						name="PHONE"
						label="Work"
						value={data.PHONE}
						handleParentChange={handleChangeInput}
						viewMode={viewMode}
						deleted={deleted}
					/>
					{/* Kore assigns this when the investor's lead is created; never typed here. */}
					<Input
						valueType="text"
						name="LEAD_ID"
						label="Lead ID"
						value={data.LEAD_ID}
						handleParentChange={handleChangeInput}
						viewMode={true}
						deleted={deleted}
					/>
					<SelectFirm
						valueKey="FIRM_ID"
						label="Firm"
						value={data.FIRM_ID}
						firmName={data.FIRM_NAME}
						handleParentChange={handleChangeDropdown}
						viewMode={viewMode}
						deleted={deleted}
						withAddNew={true}
						addNewItem={addNewItem}
						newFirm={newFirm}
					/>
					<Input
						valueType="text"
						name="ADDRESS"
						label="Address"
						value={data.ADDRESS}
						handleParentChange={handleChangeInput}
						viewMode={viewMode}
						deleted={deleted}
					/>
					<div style={{display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px'}}>
						<Input
							valueType="text"
							name="CITY"
							label="City"
							value={data.CITY}
							handleParentChange={handleChangeInput}
							viewMode={viewMode}
							deleted={deleted}
						/>
						<Input
							valueType="text"
							name="STATE"
							label="State"
							value={data.STATE}
							handleParentChange={handleChangeInput}
							viewMode={viewMode}
							deleted={deleted}
						/>
						<Input
							valueType="text"
							name="ZIP"
							label="Zip"
							value={data.ZIP}
							handleParentChange={handleChangeInput}
							viewMode={viewMode}
							deleted={deleted}
						/>
					</div>
					{/* Fields of the Kore lead, so an investor without one has none of them. */}
					{hasLeadId && (
						<React.Fragment>
							<div className="investor-info_active-buyer">
								<Checkbox
									text="ACTIVE BUYER"
									type={viewMode || deleted ? "uncheckable" : "checkable"}
									isSelected={activeBuyer}
									handleClick={() => onActiveBuyerChange(!activeBuyer)}
									name="ACTIVE_BUYER"
								/>
							</div>
							<div className="investor-info_deceased">
								<Checkbox
									text="DECEASED"
									type={viewMode || deleted ? "uncheckable" : "checkable"}
									isSelected={deceased}
									handleClick={() => onDeceasedChange(!deceased)}
									name="DECEASED"
								/>
							</div>
							{(deceased || data.SECOND_CONTACT_ID) && (
								<div>
									<SelectInvestor
										key={data.ARCIMEDES_ROW_ID}
										value={data.SECOND_CONTACT_ID ? String(data.SECOND_CONTACT_ID) : ''}
										valueKey="SECOND_CONTACT_ID"
										label="2nd Contact"
										data={data}
										handleParentChange={onSecondContactChange}
										excludeInvestorIds={[String(data.ARCIMEDES_ROW_ID)]}
										viewMode={viewMode}
										deleted={deleted}
										viewModeExtra={secondContact ? (
											<LinkComp to={`${arc.path.base}investor/${secondContact.ARCIMEDES_ROW_ID}`}>Open</LinkComp>
										) : null}
									/>
									{secondContact && (secondContact.PHONE || secondContact.EMAIL) && (
										<div className="investor-info_second-contact">
											{[secondContact.PHONE && formatPhoneNumber(secondContact.PHONE), secondContact.EMAIL].filter(Boolean).join(' · ')}
										</div>
									)}
								</div>
							)}
						</React.Fragment>
					)}
				</div>

				{hasLeadId && (
					<div style={{
						marginTop: '20px',
						padding: '12px 16px',
						backgroundColor: '#e8f5e9',
						borderRadius: '8px',
						border: '1px solid #c8e6c9'
					}}>
						<div style={{
							display: 'flex',
							alignItems: 'center',
							gap: '8px',
							marginBottom: '6px'
						}}>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2">
								<polyline points="20 6 9 17 4 12"></polyline>
							</svg>
							<span style={{fontSize: '13px', fontWeight: '600', color: '#2e7d32'}}>
								CRM Link Available
							</span>
						</div>
						<OpenInCrmButton leadId={data.LEAD_ID} entityType="lead" />
					</div>
				)}

				{onShowNotes && (
					<div className="investor-info_notes">
						<span className="investor-info_notes-label">Notes</span>
						{latest ? (
							<React.Fragment>
								{latest.when && <p className="investor-info_notes-stamp">{latest.when}</p>}
								<p className="investor-info_notes-text">{latest.text}</p>
							</React.Fragment>
						) : (
							!notesError && <p className="investor-info_notes-text investor-info_notes-empty">-</p>
						)}
						{notesError && <p className="investor-info_notes-error">{notesError}</p>}
						<button type="button" className="investor-info_show-notes-btn" onClick={onShowNotes}>
							Show Notes
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

export default InvestorInfo;
